import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import ReactECharts, { EChartsOption } from "echarts-for-react";
import { untagggedCostData } from "service/service";
import LoaderComponent from "components/Loader";

interface UntaggedPieChartProps {
  startDate: string;
  endDate: string;
  env: string;
  sector: string;
  location: string;
  selectedDateInString: string;
}

const compactFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  compactDisplay: "short",
  maximumFractionDigits: 2,
});

/** Helper: Format legend labels from IDs */
const formatLegendLabel = (id: string) => {
  if (!id) return "";

  return id
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/** Hook: Handle ECharts resizing */
const useChartResize = (chartRef: React.RefObject<any>) => {
  useEffect(() => {
    const currentChart = chartRef.current;
    if (!currentChart) return;

    const chartInstance = currentChart.getEchartsInstance();
    const chartDom = chartInstance.getDom();

    if (!chartDom) return;

    const resizeObserver = new ResizeObserver(() => {
      chartInstance.resize();
    });

    resizeObserver.observe(chartDom);

    return () => resizeObserver.disconnect();
  }, [chartRef]);
};

/** Component: Displays title and total in the top-left corner */
const TotalDisplay: React.FC<{
  isSmall: boolean;
  value: string;
}> = ({ isSmall, value }) => (
  <Box
    sx={{
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      justifyContent: "flex-start",
      textAlign: "left",
      color: "#000000",
      boxSizing: "border-box",
      mb: 0.5,
      pt: 0.5,
      pl: 0,
    }}
  >
    <Typography
      variant={isSmall ? "subtitle1" : "h6"}
      sx={{
        mb: 0.5,
        fontWeight: 500,
        textAlign: "left",
      }}
    >
      Untagged Resources
    </Typography>

    <Typography
      variant={isSmall ? "h4" : "h3"}
      sx={{
        fontWeight: 400,
        lineHeight: 1.1,
        textAlign: "left",
      }}
    >
      {value}
    </Typography>
  </Box>
);

/** Component: Displays the pie chart centered horizontally */
const ChartContainer: React.FC<{
  chartRef: React.RefObject<any>;
  option: EChartsOption;
  onEvents: any;
  isSmall: boolean;
}> = ({ chartRef, option, onEvents, isSmall }) => (
  <Box
    sx={{
      width: "100%",
      maxWidth: 700,
      minWidth: 0,
      mx: "auto",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      flex: 1,
    }}
  >
    <ReactECharts
      ref={chartRef}
      option={option}
      onEvents={onEvents}
      style={{
        height: isSmall ? 250 : 300,
        width: "100%",
      }}
    />
  </Box>
);

/** Helper: Generate chart options */
const getUntaggedChartOptions = (
  data: any[],
  isSmall: boolean,
  isMedium: boolean
): EChartsOption => {
  let radius = "58%";

  if (isSmall) {
    radius = "46%";
  } else if (isMedium) {
    radius = "52%";
  }

  return {
    tooltip: {
      backgroundColor: "#ffffff",
      trigger: "item",
      confine: true,
      textStyle: {
        color: "#000000",
      },
    },
    color: ["#00BF7D", "#299BFF", "#B3C7F7", "#8BABF1", "#5BA300", "#F57600", "#E6308A", "#F8B8D0"],
    legend: {
      orient: isSmall ? "vertical" : "horizontal",
      bottom: 0,
      left: "center",
      textStyle: {
        color: "#000000",
        fontSize: isSmall ? 10 : 12,
      },
    },
    series: [
      {
        name: "Untagged Resources",
        type: "pie",
        radius,
        center: ["50%", "37%"],
        data,
        avoidLabelOverlap: false,
        label: {
          show: !isSmall,
          position: "inside",
          color: "#fff",
          fontSize: isSmall ? 11 : 15,
          formatter: (params: any) =>
            compactFormatter.format(Number(params.value ?? 0)),
        },
        emphasis: {
          scale: true,
          scaleSize: 6,
          itemStyle: {
            shadowBlur: 8,
            shadowOffsetX: 0,
            shadowColor: "rgba(0, 0, 0, 0.4)",
          },
        },
      },
    ],
  };
};

const UntaggedPieChart: React.FC<UntaggedPieChartProps> = ({
  startDate,
  endDate,
  env,
  sector,
  location,
  selectedDateInString,
}) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [untagggedCost, setUntagggedCost] = useState<
    { _id: string; untagged_cluster_count: number }[]
  >([]);

  const chartRef = useRef<any>(null);

  const theme = useTheme();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(
    theme.breakpoints.between("sm", "md")
  );

  useChartResize(chartRef);

  /** Fetch untagged resource data */
  const untagggedCostFetch = useCallback(async () => {
    try {
      setLoading(true);

      const start = startDate.split(" ")[0];
      const end = endDate.split(" ")[0];

      const resp = await untagggedCostData(
        start,
        end,
        env,
        sector,
        location,
        selectedDateInString
      );

      setUntagggedCost(
        resp?.success ? resp.data || [] : []
      );
    } catch (err) {
      console.error("Failed to fetch untagged clusters:", err);
      setUntagggedCost([]);
    } finally {
      setLoading(false);
    }
  }, [
    startDate,
    endDate,
    env,
    sector,
    location,
    selectedDateInString,
  ]);

  useEffect(() => {
    untagggedCostFetch();
  }, [untagggedCostFetch]);

  /** Prepare chart data and calculate total */
  const { untaggedData, formattedTotal } = useMemo(() => {
    const data = untagggedCost.map((item) => ({
      name: formatLegendLabel(item._id),
      value: item.untagged_cluster_count,
      originalId: item._id,
    }));

    const sum = data.reduce(
      (acc, curr) => acc + curr.value,
      0
    );

    return {
      untaggedData: data,
      formattedTotal: compactFormatter.format(sum),
    };
  }, [untagggedCost]);

  /** Generate chart options */
  const chartOptions = useMemo(
    () =>
      getUntaggedChartOptions(
        untaggedData,
        isSmallScreen,
        isMediumScreen
      ),
    [untaggedData, isSmallScreen, isMediumScreen]
  );

  /** Navigate when a pie slice is clicked */
  const handlePieClick = (params: any) => {
    const typeId = params.data?.originalId || params.name;

    navigate(
      `/databricksADB/untagged-list?type=${encodeURIComponent(typeId)}`,
      {
        state: {
          startDate,
          endDate,
          env,
          sector,
          location,
          selectedDateInString,
        },
      }
    );
  };

  /** Loading state */
  if (loading) {
    return (
      <Box
        sx={{
          height: isSmallScreen ? "auto" : 380,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <LoaderComponent size={50} />
      </Box>
    );
  }

  /** Empty state */
  if (untagggedCost.length === 0) {
    return (
      <Box
        sx={{
          height: isSmallScreen ? "auto" : 380,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
        }}
      >
        No data available
      </Box>
    );
  }

  /** Main layout */
  return (
    <Box
      sx={{
        width: "100%",
        height: isSmallScreen ? "auto" : "100%",
        maxHeight: isSmallScreen ? "none" : 390,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "flex-start",
        px: 2,
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      {/* Title and total aligned to the top-left */}
      <TotalDisplay
        isSmall={isSmallScreen}
        value={formattedTotal}
      />

      {/* Pie chart centered horizontally and moved up */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flex: 1,
        }}
      >
        <ChartContainer
          chartRef={chartRef}
          option={chartOptions}
          onEvents={{
            click: handlePieClick,
          }}
          isSmall={isSmallScreen}
        />
      </Box>
    </Box>
  );
};

export default UntaggedPieChart;
